import { NavBarBtnModel } from 'common/src/models';
import { ProductTypes } from 'common/src/modules/products/product-types';
import {
  ControlTypeEnum,
  InputDropdownTypeEnum,
  PositionInfoListModel,
  PositionItemTypeEnum
} from 'common/src/modules/position-card/position-card-models.model';
import { CORRECTION_POSITION_TYPES_LIST } from '../../../accounting/accounting.constants';
import { PositionGroupingEnum, PriceOptionEnum } from '../../../shared/enums';
import { OutgoingInvoiceModel } from '../../models';
import { OinTypeEnum } from '../../enums';
import { PositionGroupModel } from '../../../shared/models';

export function getPositionsButtons(invoice: OutgoingInvoiceModel, positionsGroups?: PositionGroupModel[]): NavBarBtnModel[] {
  positionsGroups = positionsGroups.filter(g => !g.system || g.isDefault);
  const buttons: NavBarBtnModel[] = [
    {
      classes: 'btn-white',
      text: 'POSITIONS_GROUPING.MOVE_TO_GROUP',
      multipleText: 'POSITIONS_GROUPING.MOVE_TO_GROUP',
      badgeClass: 'rnpl-badge--grey',
      iconName: 'shuffle',
      actionName: 'onBulkMoveToGroup',
      backdropClass: 'mb-48',
      showCondition: invoice.positionGroupingOption === PositionGroupingEnum.USER_DEFINED
        && invoice.type === OinTypeEnum.REGULAR
        && positionsGroups.length > 1,
      actions: [
        {
          actionName: 'onBulkMoveToGroup',
          actionTitle: 'POSITIONS_GROUPING.MOVE_TO_GROUP',
          actionIcon: 'shuffle'
        },
        {
          actionName: 'onBulkCreateGroup',
          actionTitle: 'POSITIONS_GROUPING.CREATE_GROUP',
          actionIcon: 'plus'
        },
      ]
    },
    {
      classes: 'btn-white',
      text: 'POSITIONS_GROUPING.CREATE_GROUP',
      multipleText: 'POSITIONS_GROUPING.CREATE_GROUP',
      badgeClass: 'rnpl-badge--grey',
      iconName: 'plus',
      actionName: 'onBulkCreateGroup',
      backdropClass: 'mb-48',
      showCondition: invoice.positionGroupingOption === PositionGroupingEnum.USER_DEFINED
        && invoice.type === OinTypeEnum.REGULAR
        && positionsGroups.length === 1,
    },
    {
      classes: 'btn-white red',
      text: 'BUTTON.DELETE',
      multipleText: 'BUTTON.REMOVE',
      badgeClass: 'rnpl-badge--red',
      iconName: 'minus-circle',
      actionName: 'onDeletePositionsClick',
    },
  ];

  return buttons.filter(b => !b.hasOwnProperty('showCondition') || b.showCondition);
}


export function generatePositionsCardsInfo(
  productType: ProductTypes,
  productTypeForGrouping: ProductTypes,
  vatBlocked: boolean,
  isFinalInvoice: boolean,
  isRegularInvoice: boolean,
  correctionType: 'POSITIVE'|'NEGATIVE',
  editableFinalInvoicePosition: boolean,
  priceOption: PriceOptionEnum,
  smallBusiness: boolean,
  vatIso3: string,
): PositionInfoListModel[] {
  const prepaymentPosition = productType === ProductTypes.PREPAYMENT;
  const prepaymentPositionRegularInvoice = isRegularInvoice && productType === ProductTypes.PREPAYMENT;

  let positionInfoList: PositionInfoListModel[] = [
    {
      label: 'COLUMN.NAME',
      prop: 'description',
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.TEXTAREA,
      disabled: (isFinalInvoice && !editableFinalInvoicePosition) || prepaymentPositionRegularInvoice,
      hideWithProductTypeGrouping: [
        ProductTypes.GOODS,
        ProductTypes.SERVICES,
        ProductTypes.DIGITAL,
        ProductTypes.BUNDLE,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.ARBITRARY,
      ],
    },
    {
      label: 'FORM.DESCRIPTION',
      prop: 'productDescription',
      disabled: (isFinalInvoice && !editableFinalInvoicePosition),
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.TEXT_EDITOR,
      hideWithProductTypeGrouping: [
        ProductTypes.SERVICES,
        ProductTypes.ARBITRARY,
        ProductTypes.CORRECTION,
        ProductTypes.CREDIT,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.INVOICE,
        ProductTypes.PREPAYMENT,
        ProductTypes.CREDIT_NOTE,
      ],
    },
    {
      label: 'FORM.DESCRIPTION',
      prop: 'productDescription',
      disabled: ((isFinalInvoice && !editableFinalInvoicePosition) && correctionType === 'NEGATIVE') && prepaymentPosition,
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.TEXT_EDITOR,
      hideWithProductTypeGrouping: [
        ProductTypes.GOODS,
        ProductTypes.SERVICES,
        ProductTypes.DIGITAL,
        ProductTypes.BUNDLE,
        ProductTypes.ARBITRARY,
        ProductTypes.CORRECTION,
        ProductTypes.CREDIT,
        ProductTypes.OVERDUE_FEE
      ],
    },
    {
      label: 'FORM.DESCRIPTION',
      prop: 'providedServices',
      disabled: (isFinalInvoice && !editableFinalInvoicePosition),
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.TEXT_EDITOR,
      hideWithProductTypeGrouping: [
        ProductTypes.GOODS,
        ProductTypes.DIGITAL,
        ProductTypes.BUNDLE,
        ProductTypes.CORRECTION,
        ProductTypes.CREDIT,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.INVOICE,
        ProductTypes.CREDIT_NOTE,
      ],
    },
    {
      label: 'FORM.SPECIFY_AMOUNTS_SEPARATELY',
      prop: 'specifyAmountsSeparately',
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.TOGGLE,
      itemClass: 'col-12',
      isOptional: true,
      disabled: (isFinalInvoice && !editableFinalInvoicePosition),
      hideWithProductType: [
        ProductTypes.ARBITRARY,
        ProductTypes.GOODS,
        ProductTypes.SERVICES,
        ProductTypes.DIGITAL,
        ProductTypes.BUNDLE,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.INVOICE,
        ProductTypes.CREDIT_NOTE,
        ProductTypes.CREDIT,
      ],
    },
    {
      label: 'COLUMN.TYPE',
      prop: 'correctionType',
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.DROPDOWN,
      listItems: CORRECTION_POSITION_TYPES_LIST,
      bindLabel: 'label',
      bindValue: 'value',
      // disabled: true || (isFinalInvoice && !editableFinalInvoicePosition),
      disabled: true,
      hideWithProductTypeGrouping: [
        ProductTypes.ARBITRARY,
        ProductTypes.GOODS,
        ProductTypes.SERVICES,
        ProductTypes.DIGITAL,
        ProductTypes.BUNDLE,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.INVOICE,
        ProductTypes.CREDIT_NOTE,
        ProductTypes.CREDIT,
      ],
    },
    {
      label: 'COLUMN.QUANTITY',
      prop: 'quantity',
      unitType: 'unitType',
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.INPUT_DROPDOWN,
      disabled: (isFinalInvoice && !editableFinalInvoicePosition),
      inputDropdownType: InputDropdownTypeEnum.NUMBER,
      hideWithProductType: [
        ProductTypes.CORRECTION,
        ProductTypes.CREDIT,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.INVOICE,
        ProductTypes.CREDIT_NOTE,
      ]
    },
    {
      label: 'COLUMN.UNIT_PRICE_NET',
      prop: 'netUnitPrice',
      suffix: '€',
      disabled: (isFinalInvoice && !editableFinalInvoicePosition),
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.INPUT_DROPDOWN,
      inputDropdownType: InputDropdownTypeEnum.CURRENCY,
      showCondition: (priceOption === PriceOptionEnum.NET) || smallBusiness,
      hideWithProductType: [
        ProductTypes.CORRECTION,
        ProductTypes.CREDIT,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.INVOICE,
        ProductTypes.CREDIT_NOTE,
      ],
      showUnitOnly: true,
    },
    {
      label: 'COLUMN.UNIT_PRICE_GROSS',
      prop: 'grossUnitPrice',
      suffix: '€',
      disabled: (isFinalInvoice && !editableFinalInvoicePosition),
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.INPUT_DROPDOWN,
      inputDropdownType: InputDropdownTypeEnum.CURRENCY,
      hideWithProductType: [
        ProductTypes.CORRECTION,
        ProductTypes.CREDIT,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.INVOICE,
        ProductTypes.CREDIT_NOTE,
      ],
      showUnitOnly: true,
      showCondition: (priceOption === PriceOptionEnum.GROSS) && !smallBusiness
    },
    {
      label: 'COLUMN.AMOUNT_NET',
      prop: 'netTotalPrice',
      suffix: '€',
      disabled: (isFinalInvoice && !editableFinalInvoicePosition) || prepaymentPositionRegularInvoice,
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.INPUT_DROPDOWN,
      inputDropdownType: InputDropdownTypeEnum.CURRENCY,
      currencyNegativeRow: true,
      showUnitOnly: true,
      showCondition: ((priceOption === PriceOptionEnum.NET) || smallBusiness)
        || productType === ProductTypes.CORRECTION
        || productType === ProductTypes.CREDIT
        || productType === ProductTypes.OVERDUE_FEE
        || productType === ProductTypes.PREPAYMENT
        || productType === ProductTypes.INVOICE
        || productType === ProductTypes.CREDIT_NOTE
    },
    {
      label: 'COLUMN.VAT',
      prop: 'vat',
      suffix: '%',
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.VAT_RATE,
      disabled: vatBlocked || (isFinalInvoice && !editableFinalInvoicePosition) || prepaymentPosition,
      vatBlocked: vatBlocked,
      vatIso3: vatIso3,
      hideWithProductType: [
        ProductTypes.ARBITRARY,
        ProductTypes.GOODS,
        ProductTypes.SERVICES,
        ProductTypes.DIGITAL,
        ProductTypes.BUNDLE,
        ProductTypes.CORRECTION,
        ProductTypes.INVOICE,
        ProductTypes.CREDIT_NOTE,
      ]
    },
    {
      label: 'COLUMN.VAT',
      prop: 'vatTotal',
      suffix: '€',
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.INPUT_DROPDOWN,
      inputDropdownType: InputDropdownTypeEnum.CURRENCY,
      currencyNegativeRow: true,
      currencySourceFormat: true,
      disabled: vatBlocked || (isFinalInvoice && !editableFinalInvoicePosition) || prepaymentPosition,
      vatBlocked: vatBlocked,
      showUnitOnly: true,
      hideWithProductType: [
        ProductTypes.ARBITRARY,
        ProductTypes.SERVICES,
        ProductTypes.GOODS,
        ProductTypes.DIGITAL,
        ProductTypes.BUNDLE,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.CREDIT,
      ]
    },
    {
      label: 'COLUMN.AMOUNT_GROSS',
      prop: 'grossTotalPrice',
      suffix: '€',
      disabled: (isFinalInvoice && !editableFinalInvoicePosition) || prepaymentPositionRegularInvoice,
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.INPUT_DROPDOWN,
      currencyNegativeRow: true,
      inputDropdownType: InputDropdownTypeEnum.CURRENCY,
      showUnitOnly: true,
      showCondition: ((priceOption === PriceOptionEnum.GROSS) && !smallBusiness)
        || productType === ProductTypes.CORRECTION
        || productType === ProductTypes.CREDIT
        || productType === ProductTypes.OVERDUE_FEE
        || productType === ProductTypes.PREPAYMENT
        || productType === ProductTypes.INVOICE
        || productType === ProductTypes.CREDIT_NOTE
    },
  ];

  positionInfoList = positionInfoList
    .filter((item) => !item.hideWithProductTypeGrouping || !item.hideWithProductTypeGrouping.includes(productTypeForGrouping))
    .filter((item) => !item.hideWithProductType || !item.hideWithProductType.includes(productType))
    .filter((item) => !item.hasOwnProperty('showCondition') || item.showCondition);

  return positionInfoList;

}

export function topBlockControls(
  productType: ProductTypes,
  vatBlocked: boolean,
  isFinalInvoice: boolean,
  isRegularInvoice: boolean,
  editableFinalInvoicePosition: boolean,
  priceOption: PriceOptionEnum,
  smallBusiness: boolean,
): PositionInfoListModel[] {
  const prepaymentPositionRegularInvoice = isRegularInvoice && productType === ProductTypes.PREPAYMENT;

  let positionInfoList: PositionInfoListModel[] = [
    {
      label: 'COLUMN.UNIT_PRICE_NET',
      prop: 'netUnitPrice',
      suffix: '€',
      disabled: (isFinalInvoice && !editableFinalInvoicePosition),
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.INPUT_DROPDOWN,
      inputDropdownType: InputDropdownTypeEnum.CURRENCY,
      showCondition: (priceOption === PriceOptionEnum.GROSS) && !smallBusiness,
      hideWithProductType: [
        ProductTypes.CORRECTION,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.INVOICE,
        ProductTypes.CREDIT_NOTE,
      ],
      showUnitOnly: true,
    },
    {
      label: 'COLUMN.UNIT_PRICE_GROSS',
      prop: 'grossUnitPrice',
      suffix: '€',
      disabled: (isFinalInvoice && !editableFinalInvoicePosition) || smallBusiness || vatBlocked,
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.INPUT_DROPDOWN,
      inputDropdownType: InputDropdownTypeEnum.CURRENCY,
      hideWithProductType: [
        ProductTypes.CORRECTION,
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.INVOICE,
        ProductTypes.CREDIT_NOTE,
      ],
      showUnitOnly: true,
      showCondition: (priceOption === PriceOptionEnum.NET) || smallBusiness
    },
    {
      label: 'COLUMN.AMOUNT_NET',
      prop: 'netTotalPrice',
      suffix: '€',
      disabled: (isFinalInvoice && !editableFinalInvoicePosition) || prepaymentPositionRegularInvoice,
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.INPUT_DROPDOWN,
      inputDropdownType: InputDropdownTypeEnum.CURRENCY,
      currencyNegativeRow: true,
      showUnitOnly: true,
      showCondition: ((priceOption === PriceOptionEnum.GROSS) && !smallBusiness)
        || productType === ProductTypes.INVOICE
        || productType === ProductTypes.CREDIT_NOTE,
      hideWithProductType: [
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.CORRECTION,
      ],
    },
    {
      label: 'COLUMN.AMOUNT_GROSS',
      prop: 'grossTotalPrice',
      suffix: '€',
      disabled: (isFinalInvoice && !editableFinalInvoicePosition) || prepaymentPositionRegularInvoice || smallBusiness || vatBlocked,
      type: PositionItemTypeEnum.WITH_CONTROL,
      controlType: ControlTypeEnum.INPUT_DROPDOWN,
      currencyNegativeRow: true,
      inputDropdownType: InputDropdownTypeEnum.CURRENCY,
      showUnitOnly: true,
      showCondition: (priceOption === PriceOptionEnum.NET) || smallBusiness
        || productType === ProductTypes.INVOICE
        || productType === ProductTypes.CREDIT_NOTE,
      hideWithProductType: [
        ProductTypes.OVERDUE_FEE,
        ProductTypes.PREPAYMENT,
        ProductTypes.CORRECTION,
      ],
    },
  ];

  positionInfoList = positionInfoList
    .filter((item) => !item.hideWithProductType || !item.hideWithProductType.includes(productType))
    .filter((item) => !item.hasOwnProperty('showCondition') || item.showCondition);

  return positionInfoList;

}
